import React, { useId, useState } from 'react';

const LABEL = 'text-[13px] font-semibold text-gray-900';
const INPUT = 'w-full rounded-xl border-0 bg-gray-100 px-4 py-3 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-green-400 placeholder:text-gray-400';
const SELECT = 'w-full appearance-none rounded-xl border-0 bg-gray-100 px-4 py-3 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-green-400 cursor-pointer pr-10';
const NOTE = 'text-[11px] text-gray-400';

export function TextField({
  label,
  required,
  type = 'text',
  value,
  onChange,
  placeholder,
  note,
  className = '',
  suggestions = [],
  list,
  ...rest
}) {
  const internalListId = useId();
  const normalizedSuggestions = Array.isArray(suggestions)
    ? suggestions
        .map((item) => {
          if (item === null || item === undefined) return null;
          if (typeof item === 'string' || typeof item === 'number') {
            const text = String(item).trim();
            return text ? { value: text, label: text } : null;
          }

          const value = String(item.value ?? item.code ?? item.id ?? item.name ?? item.label ?? '').trim();
          if (!value) return null;
          const suggestionLabel = String(item.label ?? item.name ?? item.title ?? value).trim() || value;
          return { value, label: suggestionLabel };
        })
        .filter(Boolean)
    : [];

  const listId = list || (normalizedSuggestions.length > 0 ? `textfield-${internalListId}` : undefined);

  const handleChange = (e) => {
    if (type === 'number') {
      const raw = e.target.value;
      if (raw === '') {
        onChange('');
        return;
      }

      const parsed = Number(raw);
      onChange(Number.isNaN(parsed) ? '' : parsed);
      return;
    }

    onChange(e.target.value);
  };

  return (
    <div className="grid gap-2">
      <label className={LABEL}>
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={type === 'number' && (value === '' || value == null || Number.isNaN(value)) ? '' : (value ?? '')}
        placeholder={placeholder || 'Enter text'}
        onChange={handleChange}
        list={listId}
        className={`${INPUT} ${className}`.trim()}
        {...rest}
      />
      {normalizedSuggestions.length > 0 && !list && (
        <datalist id={listId}>
          {normalizedSuggestions.map((suggestion) => (
            <option key={suggestion.value} value={suggestion.value}>
              {suggestion.label}
            </option>
          ))}
        </datalist>
      )}
      {note && <span className={NOTE}>{note}</span>}
    </div>
  );
}

export function PasswordField({
  label,
  required,
  value,
  onChange,
  placeholder,
  note,
  className = '',
  ...rest
}) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="grid gap-2">
      <label className={LABEL}>
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || 'Enter password'}
          className={`${INPUT} pr-11 ${className}`.trim()}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          tabIndex={-1}
          aria-label={visible ? 'Hide password' : 'Show password'}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {visible ? (
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.584 10.587a2 2 0 002.829 2.829M9.363 5.365A9.466 9.466 0 0112 5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.284M6.228 6.228A10.451 10.451 0 001.935 12.5C3.226 16.836 7.244 20 12 20a9.46 9.46 0 004.635-1.223" />
            </svg>
          ) : (
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          )}
        </button>
      </div>
      {note && <span className={NOTE}>{note}</span>}
    </div>
  );
}

export function SelectField({
  label,
  required,
  value,
  onChange,
  options = [],
  note,
  placeholder = 'Select Item',
  emptyOptionLabel,
  emptyOptionDisabled = true,
  disabled = false,
  className = '',
  ...rest
}) {
  const hasCustomEmptyOption = emptyOptionLabel !== undefined && emptyOptionLabel !== null;

  return (
    <div className="grid gap-2">
      <label className={LABEL}>
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <div className="relative">
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={`${SELECT} disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed disabled:opacity-70 ${className}`.trim()}
          disabled={disabled}
          {...rest}
        >
          {hasCustomEmptyOption ? (
            <option value="" disabled={emptyOptionDisabled}>
              {emptyOptionLabel}
            </option>
          ) : (
            <option value="" disabled>{placeholder}</option>
          )}
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
          fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <polyline points="6 9 12 15 18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {note && <span className={NOTE}>{note}</span>}
    </div>
  );
}

// Pill/chip segmented control — radio-style (single select)
export function SegmentedField({ label, required, value, onChange, options, note }) {
  return (
    <div className="grid gap-2">
      <label className={LABEL}>
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o.value} type="button" onClick={() => onChange(o.value)}
            className={`rounded-lg border px-3.5 py-2 text-[12.5px] font-medium transition-all ${
              value === o.value
                ? 'border-green-200 bg-green-50 font-semibold text-green-700'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
            }`}>
            {o.label}
          </button>
        ))}
      </div>
      {note && <span className={NOTE}>{note}</span>}
    </div>
  );
}

// Toggle switch
export function ToggleField({ label, value, onChange, note }) {
  return (
    <div className="grid gap-2">
      <label className={LABEL}>{label}</label>
      <button type="button" role="switch" aria-checked={!!value} onClick={() => onChange(!value)}
        className="inline-flex items-center gap-2.5 w-fit">
        <span className={`relative h-[22px] w-10 rounded-full transition-colors ${value ? 'bg-green-500' : 'bg-gray-300'}`}>
          <span className={`absolute left-0.5 top-0.5 h-[18px] w-[18px] rounded-full bg-white transition-transform ${value ? 'translate-x-[18px]' : ''}`} />
        </span>
        <span className="text-[13px] text-gray-500">{value ? 'Yes' : 'No'}</span>
      </button>
      {note && <span className={NOTE}>{note}</span>}
    </div>
  );
}

// Boolean radio checkboxes (True / False) — for sex_disaggregation_required
export function BooleanField({ label, required, value, onChange, note }) {
  return (
    <div className="grid gap-2">
      <label className={LABEL}>
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <div className="flex gap-5">
        {[{ v: true, l: 'True' }, { v: false, l: 'False' }].map(({ v, l }) => (
          <label key={l} className="flex items-center gap-1.5 cursor-pointer">
            <input type="radio" checked={value === v} onChange={() => onChange(v)}
              className="w-[15px] h-[15px] accent-green-600" />
            <span className="text-sm text-gray-700">{l}</span>
          </label>
        ))}
      </div>
      {note && <span className={NOTE}>{note}</span>}
    </div>
  );
}

// Textarea
export function TextareaField({ label, required, value, onChange, placeholder, rows = 3, note }) {
  return (
    <div className="grid gap-2">
      <label className={LABEL}>
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Enter text'}
        rows={rows}
        className="w-full rounded-xl bg-gray-100 px-4 py-3 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-green-400 placeholder:text-gray-400 resize-y border-0"
      />
      {note && <span className={NOTE}>{note}</span>}
    </div>
  );
}
